/**
 * Google Analytics Event Tracking Utility
 * Provides type-safe event tracking for key user actions
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

type GAEventName =
  | 'rig_created'
  | 'rig_deleted'
  | 'camera_selected'
  | 'accessory_added'
  | 'accessory_removed'
  | 'pdf_exported'
  | 'user_registered'
  | 'user_logged_in'
  | 'project_created'
  | 'project_deleted'
  | 'rig_shared'
  | 'guided_builder_started'
  | 'guided_builder_completed';

interface GAEventParams {
  rig_created: { rig_name?: string; camera_name?: string; rig_style?: string };
  rig_deleted: { rig_name?: string };
  camera_selected: { camera_name: string; camera_brand?: string };
  accessory_added: { accessory_name: string; accessory_category?: string };
  accessory_removed: { accessory_name: string; accessory_category?: string };
  pdf_exported: { rig_name?: string; include_signal_flow?: boolean };
  user_registered: { method?: string };
  user_logged_in: { method?: string };
  project_created: { project_name?: string };
  project_deleted: { project_name?: string };
  rig_shared: { share_method?: string };
  guided_builder_started: { camera_name?: string };
  guided_builder_completed: { camera_name?: string; accessory_count?: number };
}

/**
 * Track a custom event in Google Analytics
 * @param eventName - The name of the event to track
 * @param params - Optional parameters for the event
 */
export function trackEvent<T extends GAEventName>(
  eventName: T,
  params?: GAEventParams[T]
): void {
  // Analytics disabled: intentional no-op.
  void eventName;
  void params;
}

/**
 * Get the category for an event (for GA4 grouping)
 */
function getEventCategory(eventName: GAEventName): string {
  const categories: Record<GAEventName, string> = {
    rig_created: 'Rig Management',
    rig_deleted: 'Rig Management',
    camera_selected: 'Rig Building',
    accessory_added: 'Rig Building',
    accessory_removed: 'Rig Building',
    pdf_exported: 'Export',
    user_registered: 'Authentication',
    user_logged_in: 'Authentication',
    project_created: 'Project Management',
    project_deleted: 'Project Management',
    rig_shared: 'Sharing',
    guided_builder_started: 'Guided Builder',
    guided_builder_completed: 'Guided Builder',
  };
  return categories[eventName];
}

/**
 * Track a page view (useful for SPAs with dynamic routing)
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  // Analytics disabled: intentional no-op.
  void pagePath;
  void pageTitle;
}
